import { Injectable } from '@angular/core';
import { Observable, catchError, tap, throwError } from 'rxjs';
import { ENDPOINTS } from '../shared/constants/url.constants';
import { GlobalErrorHandlerService } from '../shared/services/global-error-handler.service';
import { HttpRequestService } from '../shared/services/http-request.service';
import { NotificationService } from '../shared/services/notification.service';
import { formatUrl } from '../shared/utils/format-url-util';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexPlotOptions,
  ApexYAxis,
  ApexLegend,
  ApexStroke,
  ApexXAxis,
  ApexFill,
  ApexTooltip
} from "ng-apexcharts";
import { HttpClient, HttpContext, HttpHeaders, HttpResponse } from '@angular/common/http';
import { PRESERVE_ORIGINAL_ERROR, SUPPRESS_ERROR_TOAST } from '../interceptors/error.interceptor';


export interface ChartOptions {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
  yaxis: ApexYAxis;
  xaxis: ApexXAxis;
  fill: ApexFill;
  tooltip: ApexTooltip;
  stroke: ApexStroke;
  legend: ApexLegend;
}

@Injectable({
  providedIn: 'root',
})
export class BillingService {
  constructor(
    private httpClient: HttpRequestService,
    private http: HttpClient,
    private errorHandler: GlobalErrorHandlerService,
    private notificationService: NotificationService
  ) {}

  formatCurrency(amount: number | string): string {
    if (typeof amount === 'string' && amount.trim().startsWith('$')) {
      return amount;
    }

    if(amount == null) {
      return amount;
    }
    const numericAmount = typeof amount === 'string' ? parseFloat(amount.replace(/,/g, '').trim()): amount;

    if (isNaN(numericAmount)) {
      return '-';
    }
  
    return `$${numericAmount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  getBillingFilterDropdown(
    programCode: any,
    isAfterUpdate = false
  ): Observable<any> {
    const url = formatUrl(ENDPOINTS.BILLING.GET_BILLING_DROPDOWN_VALUES, {
      programCode,
    });
    return this.httpClient.get(url, isAfterUpdate).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  getIgnoreReasonDropdown(
    programCode: any,
    isAfterUpdate = false
  ): Observable<any> {
    const url = formatUrl(ENDPOINTS.BILLING.GET_IGNORE_REASON_DROPDOWN_VALUES, {
      programCode,
    });
    return this.httpClient.get(url, isAfterUpdate).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  getLookupDomainDropdown(
    programCode: any,
    isAfterUpdate = false
  ): Observable<any> {
    const url = formatUrl(ENDPOINTS.BILLING.GET_LOOKUP_DOMAIN_DROPDOWN_VALUES, {
      programCode,
    });
    return this.httpClient.get(url, isAfterUpdate).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  getBatchReportData(payload: any, isAfterUpdate = false): Observable<any> {
    return this.httpClient
      .post(ENDPOINTS.BILLING.GET_BATCH_REPORT, payload, isAfterUpdate)
      .pipe(
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
    );
  }

  getClaimReportGridData(payload: any, isAfterUpdate = false): Observable<any> {
    return this.httpClient
      .post(ENDPOINTS.BILLING.GET_CLAIM_REPORT_GRID, payload, isAfterUpdate)
      .pipe(
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      );
  }

  getHierarchyData(payload: any, isAfterUpdate = false): Observable<any> {
    return this.httpClient
      .post(ENDPOINTS.BILLING.GET_HIERARCHY, payload, isAfterUpdate)
      .pipe(
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      );
  }

  getReports(options: {
    reportName: string;
    mcoName?: string;
    claimType?: string;
    batchDate?: string;
    reportBy?: string;
    splitBy?: string;
    ignoreReason?: string;
    claimFieldName?: string;
    batchClaimId?: string;
    programCode?: string;
    resubDate?: any;
    resubReason?: string;
  }, isAfterUpdate = false): Observable<any> {
    const payload = {
      reportName: options.reportName ?? null,
      mcoName: options.mcoName ?? null,
      claimType: options.claimType ?? null,
      batchDate: options.batchDate ?? null,
      reportBy: options.reportBy ?? null,
      splitBy: options.splitBy ?? null,
      ignoreReason: options.ignoreReason ?? null,
      claimFieldName: options.claimFieldName ?? null,
      batchClaimId: options.batchClaimId ?? null,
      programCode: options.programCode ?? null,
      resubDate: options.resubDate ?? null,
      resubReason: options.resubReason ?? null,
    };
    return this.httpClient.post(ENDPOINTS.BILLING.GET_REPORTS, payload, isAfterUpdate).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  getBatchDates(programCode: any, isAfterUpdate = false): Observable<any> {
    const url = formatUrl(ENDPOINTS.BILLING.GET_BATCH_DATES, {
      programCode,
    });
    return this.httpClient.get(url, isAfterUpdate).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  getResubDates(programCode: any, isAfterUpdate = false): Observable<any> {
    const url = formatUrl(ENDPOINTS.BILLING.GET_RESUB_DATES, {
      programCode
    });
    return this.httpClient.get(url, isAfterUpdate).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  getResubReason(programCode: any, isAfterUpdate = false): Observable<any> {
    const url = formatUrl(ENDPOINTS.BILLING.GET_RESUB_REASONS, {
      programCode,
    });
    return this.httpClient.get(url, isAfterUpdate).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  getClaimDetails(
    programCode: any,
    claimId: any,
    isAfterUpdate = false
  ): Observable<any> {
    const url = formatUrl(ENDPOINTS.BILLING.GET_CLAIM_DETAILS, {
      programCode,
      claimId,
    });
    return this.httpClient.get(url, isAfterUpdate).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  getResubDetails(programCode: any,
    id: any,
    isAfterUpdate = false
  ): Observable<any> {
    const url = formatUrl(ENDPOINTS.BILLING.GET_RESUB_DETAILS, {
      programCode,
      id,
    });
    return this.httpClient.get(url, isAfterUpdate).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  getIgnoredTimesheets(
    programCode: any,
    id: any,
    isAfterUpdate = false
  ): Observable<any> {
    const url = formatUrl(ENDPOINTS.BILLING.GET_IGNORED_TIMESHEETS, {
      programCode,
      id,
    });
    return this.httpClient.get(url, isAfterUpdate).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  getIgnoredTimesheetsSummary(
    programCode: any,
    id: any,
    isAfterUpdate = false
  ): Observable<any> {
    const url = formatUrl(ENDPOINTS.BILLING.GET_IGNORED_TIMESHEETS_SUMMARY, {
      programCode,
      id,
    });
    return this.httpClient.get(url, isAfterUpdate).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  searchIgnoredTimesheets(
    payload: any, isAfterUpdate = false
  ): Observable<any> {
    return this.httpClient.post(ENDPOINTS.BILLING.SEARCH_IGNORED_TIMESHEETS, payload, isAfterUpdate).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }
  //SAVE_WORKDAY_SALES
  saveWorkdaySales(payload: any,isAfterUpdate = false): Observable<any> {
    
    return this.httpClient.post(ENDPOINTS.BILLING.SAVE_WORKDAY_SALES, payload, isAfterUpdate).pipe(
      tap(() => {
        this.notificationService.success(
          'Success',
          `Service code Updated Successfully.`
        );
      }),
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  addAndUpdateConfig(payload: any, action: string, isAfterUpdate = false): Observable<any> {
    const actionPerformed = action === 'add' ? 'Added' : 'Updated';
    return this.httpClient.post(ENDPOINTS.ADD_UPDATE_CONFIG, payload, isAfterUpdate).pipe(
      tap(() => {
        this.notificationService.success(
          'Success',
          `Config ${actionPerformed} Successfully.`
        );
      }),
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  deleteConfig(programCode: any, id: any, isAfterUpdate = false): Observable<any> {
    const url = formatUrl(ENDPOINTS.DELETE_CONFIG, {
      programCode, id
    });
    return this.httpClient.post(url, {}, isAfterUpdate).pipe(
      tap(() => {
        this.notificationService.success(
          'Success',
          `Config Deleted Successfully.`
        );
      }),
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  addAndUpdateLookupDomain(payload: any, action: string, isAfterUpdate = false): Observable<any> {
    const actionPerformed = action === 'add' ? 'Added' : 'Updated';
    return this.httpClient
      .post(ENDPOINTS.ADD_UPDATE_LOOKUP_DOMAIN, payload, isAfterUpdate)
      .pipe(
        tap(() => {
          this.notificationService.success(
            'Success',
            `Config ${actionPerformed} Successfully.`
          );
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      );
  }

  addAndUpdateLookupValue(payload: any, action: string, isAfterUpdate = false): Observable<any> {
    const actionPerformed = action === 'add' ? 'added' : 'updated';
    return this.httpClient
      .post(ENDPOINTS.ADD_UPDATE_LOOKUP_VALUE, payload, isAfterUpdate)
      .pipe(
        tap(() => {
          this.notificationService.success(
            'Success',
            `Lookup value ${actionPerformed} successfully.`
          );
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      );
  }

  deleteDomain(programCode: any, id: any, isAfterUpdate = false): Observable<any> {
    const url = formatUrl(ENDPOINTS.DELETE_DOMAIN, {
      programCode,
      id,
    });
    return this.httpClient.post(url, {}, isAfterUpdate).pipe(
      tap(() => {
        this.notificationService.success(
          'Success',
          `Config Deleted Successfully.`
        );
      }),
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  deleteLookupValue(programCode: any, id: any, isAfterUpdate = false): Observable<any> {
    const url = formatUrl(ENDPOINTS.DELETE_LOOKUP_VALUE, {
      programCode, id
    });
    return this.httpClient.post(url, {}, isAfterUpdate).pipe(
      tap(() => {
        this.notificationService.success(
          'Success',
          `Lookup Value Deleted Successfully.`
        );
      }),
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  getPDF(payload: any) {
    const headers = new HttpHeaders({
      'content-type': 'application/json'
    })
    return this.http.post(ENDPOINTS.GET_BILLING_PDF,payload, { responseType: 'blob' });

  }

  getConsumerLedgerList(programCode: any,
    participantPplId: any,
    pplIcn?: any,
    filters?: { mcoName?: string; startDate?: string; endDate?: string },
    isAfterUpdate = false
  ): Observable<any> {
    const baseUrl = formatUrl(ENDPOINTS.BILLING.GET_CONSUMER_LEDGER, {
      programCode,
    });
    const queryParts: string[] = [`participantPplId=${participantPplId ?? ''}`];
    if (pplIcn) {
      queryParts.push(`pplIcn=${pplIcn}`);
    }
    if (filters?.mcoName) {
      queryParts.push(`mcoName=${filters.mcoName}`);
    }
    if (filters?.startDate) {
      queryParts.push(`startDate=${filters.startDate}`);
    }
    if (filters?.endDate) {
      queryParts.push(`endDate=${filters.endDate}`);
    }
    const url = `${baseUrl}?${queryParts.join('&')}`;
    return this.httpClient.get(url, isAfterUpdate).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  claimGlobalSearch(programCode: any, pplIcn: any): Observable<any> {
    const baseUrl = formatUrl(ENDPOINTS.BILLING.CLAIM_GLOBAL_SEARCH, {
      programCode,
    });
    const url = `${baseUrl}?pplIcn=${encodeURIComponent(pplIcn)}`;
    return this.http.get(url, {
      headers: new HttpHeaders({ responseType: 'JSON' }),
      context: new HttpContext().set(PRESERVE_ORIGINAL_ERROR, true),
    });
  }

  getDepositSummary(programCode: any, isAfterUpdate = false): Observable<any> {
    const url = formatUrl(ENDPOINTS.BILLING.GET_DEPOSIT_SUMMARY, {
      programCode,
    });
    return this.httpClient.get(url, isAfterUpdate).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  getDepositBankAccounts(
    programCode: any,
    filters?: { mcoOrgName?: string; accountNumber?: string },
    isAfterUpdate = false
  ): Observable<any> {
    const baseUrl = formatUrl(ENDPOINTS.BILLING.GET_DEPOSIT_BANK_ACCOUNTS, {
      programCode,
    });
    const queryParts: string[] = [];
    if (filters?.mcoOrgName) {
      queryParts.push(`mcoOrgName=${filters.mcoOrgName}`);
    }
    if (filters?.accountNumber) {
      queryParts.push(`accountNumber=${filters.accountNumber}`);
    }
    const qs = queryParts.join('&');
    const url = qs ? `${baseUrl}?${qs}` : baseUrl;
    return this.httpClient.get(url, isAfterUpdate).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  addBillingDeposit(
    programCode: any,
    payload: {
      checkNumber: string;
      depositAmount: number;
      mcoOrgName: string;
      accountNumber: string;
      depositDate: string;
      isAdvanceDeposit: boolean;
    }
  ): Observable<any> {
    const url = formatUrl(ENDPOINTS.BILLING.ADD_BILLING_DEPOSIT, {
      programCode,
    });
    return this.httpClient.post(url, payload, true).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  getClaimForManualCash(programCode: any, pplIcn: string): Observable<any> {
    const baseUrl = formatUrl(ENDPOINTS.BILLING.GET_CLAIM_FOR_MANUAL_CASH, {
      programCode,
    });
    const url = `${baseUrl}?pplIcn=${encodeURIComponent(pplIcn)}`;
    return this.http.get(url, {
      headers: new HttpHeaders({ responseType: 'JSON' }),
      context: new HttpContext().set(PRESERVE_ORIGINAL_ERROR, true).set(SUPPRESS_ERROR_TOAST, true),
    });
  }

  applyBillingDepositManualCash(
    programCode: any,
    payload: {
      tasBillingDepositDetailId: number;
      claims: {
        tasBillingBatchClaimDetailId: number;
        amount: number;
        primaryCarc: string | null;
        primaryRarc: string | null;
      }[];
    }
  ): Observable<any> {
    const url = formatUrl(ENDPOINTS.BILLING.APPLY_BILLING_DEPOSIT_MANUAL_CASH, {
      programCode,
    });
    return this.http.post(url, payload, {
      headers: new HttpHeaders({ responseType: 'JSON' }).append('rfrw', 'true'),
      context: new HttpContext().set(PRESERVE_ORIGINAL_ERROR, true),
    });
  }

  exportDepositReport(
    programCode: any,
    startDate: string,
    endDate: string,
    reportType?: string
  ): Observable<HttpResponse<Blob>> {
    const baseUrl = formatUrl(ENDPOINTS.BILLING.EXPORT_DEPOSIT_REPORT, {
      programCode,
    });
    const queryParts = [`startDate=${startDate}`, `endDate=${endDate}`];
    if (reportType) {
      queryParts.push(`reportType=${reportType}`);
    }
    const url = `${baseUrl}?${queryParts.join('&')}`;
    return this.http.get(url, { observe: 'response', responseType: 'blob' });
  }

  getWriteOffReasons(programCode: any, isAfterUpdate = false): Observable<any> {
    const url = formatUrl(ENDPOINTS.BILLING.GET_WRITE_OFF_REASONS, {
      programCode,
    });
    return this.httpClient.get(url, isAfterUpdate).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  submitWriteOffRequest(
    programCode: any,
    payload: { tasBillingBatchClaimDetailId: number; reason: string; justification: string }
  ): Observable<any> {
    const url = formatUrl(ENDPOINTS.BILLING.SUBMIT_WRITE_OFF_REQUEST, {
      programCode,
    });
    return this.http.post(url, payload, {
      headers: new HttpHeaders({ responseType: 'JSON' }),
      context: new HttpContext().set(PRESERVE_ORIGINAL_ERROR, true),
    });
  }

  submitWriteOffDecision(
    programCode: any,
    payload: { requestIds: number[]; decision: 'APPROVE' | 'REJECT'; comment?: string }
  ): Observable<any> {
    const url = formatUrl(ENDPOINTS.BILLING.SUBMIT_WRITE_OFF_DECISION, {
      programCode,
    });
    return this.http.post(url, payload, {
      headers: new HttpHeaders({ responseType: 'JSON' }),
      context: new HttpContext().set(PRESERVE_ORIGINAL_ERROR, true),
    });
  }

  // GET billingapi/v1/globalLedgerExport/{programCode}?dosFrom=yyyy-MM-dd&dosTo=yyyy-MM-dd
  // &mcoName=...&pplIcn=...&participantPplId=... (all query params optional) -> Global
  // Ledger's Export to Excel. Same blob/observe-response pattern as exportDepositReport.
  exportGlobalLedger(
    programCode: any,
    filters?: { dosFrom?: string; dosTo?: string; mcoName?: string; pplIcn?: string; participantPplId?: string }
  ): Observable<HttpResponse<Blob>> {
    const baseUrl = formatUrl(ENDPOINTS.BILLING.GLOBAL_LEDGER_EXPORT, {
      programCode,
    });
    const queryParts: string[] = [];
    if (filters?.dosFrom) {
      queryParts.push(`dosFrom=${filters.dosFrom}`);
    }
    if (filters?.dosTo) {
      queryParts.push(`dosTo=${filters.dosTo}`);
    }
    if (filters?.mcoName) {
      queryParts.push(`mcoName=${encodeURIComponent(filters.mcoName)}`);
    }
    if (filters?.pplIcn) {
      queryParts.push(`pplIcn=${encodeURIComponent(filters.pplIcn)}`);
    }
    if (filters?.participantPplId) {
      queryParts.push(`participantPplId=${encodeURIComponent(filters.participantPplId)}`);
    }
    const url = queryParts.length ? `${baseUrl}?${queryParts.join('&')}` : baseUrl;
    return this.http.get(url, { observe: 'response', responseType: 'blob' });
  }
}
